import { WButton } from "@/components/button/WButton.tsx";
import { WModalButton } from "@/components/button/WToolButton.tsx";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { WModal } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { ModalHeaderBlock } from "@/modules/compute/ModalHeaderBlock.tsx";
import { ParamsTextField } from "@/modules/compute/ParamsTextField.tsx";
import { PasswordFields, type VmPasswordInputs } from "@/modules/compute/PasswordFields.tsx";
import { parametersByNameAtom } from "@/modules/compute/vmParametersQuery.ts";
import { changeVmUserPasswordMutation, startVmMutation, type VirtualMachineLoc } from "@/modules/compute/vmQuery.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { VmChangePasswordBody } from "@warrenio/api-spec/spec.oats.gen";
import { useForm, type SubmitHandler } from "react-hook-form";

export interface VmChangePasswordInputs extends VmChangePasswordBody, VmPasswordInputs {}

export function ChangePasswordModalContent({ vm }: { vm: VirtualMachineLoc }) {
    const { uuid, status, location, username } = vm;

    //#region Hooks
    const { t } = useLingui();
    const params = useSuspenseQueryAtom(parametersByNameAtom);
    const startMutation = useStandardMutation(startVmMutation);

    const changePasswordMutation = useStandardMutation(changeVmUserPasswordMutation);

    //#endregion

    const usernameParams = params.get("username")!;

    const onSubmit: SubmitHandler<VmChangePasswordInputs> = async (inputs) => {
        const { password, username } = inputs;

        await changePasswordMutation.mutateAsync({
            body: {
                username,
                password,
                uuid,
            },
            location,
        });
    };

    const form = useForm<VmChangePasswordInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            username,
            password: "",
            password_confirm: "",
        },
    });

    return (
        <WModalForm
            title={t`Change password`}
            label={t`Change`}
            form={form}
            onSubmit={onSubmit}
            isActionDisabled={status === "stopped"}
            footerNotice={
                status === "stopped" ? (
                    <Trans>Changing the password is only possible when the virtual machine is running.</Trans>
                ) : undefined
            }
        >
            <ModalHeaderBlock vm={vm} showSize={false}>
                <WButton
                    isDisabled={status === "running"}
                    icon="jp-icon-run"
                    variant="ghost"
                    color="primary"
                    action={async () => {
                        await startMutation.mutateAsync({ uuid, location });
                    }}
                >
                    {status === "running" ? <Trans>Running</Trans> : <Trans>Start</Trans>}
                </WButton>
            </ModalHeaderBlock>

            <p className="pt-2 pb-4">
                <Trans>The password can be changed for any existing user account.</Trans>
            </p>

            <ParamsTextField
                form={form}
                field="username"
                autoFocus
                headless
                params={{ ...usernameParams, mandatory: true }}
                autoComplete="username"
                label={t`Username`}
                // TODO: Constraint description should come from the VM parameters
                description={t`The user account for which password will be set.`}
                defaultValue={username}
            />

            <PasswordFields isNew headless />
        </WModalForm>
    );
}

export function ChangePasswordModal({ vm }: { vm: VirtualMachineLoc }) {
    const { t } = useLingui();
    return (
        <WModal button={<WModalButton label={t`Change password`} icon="jp-lock-icon" />}>
            <ChangePasswordModalContent vm={vm} />
        </WModal>
    );
}
